import * as actions from './Actions';

// Normalizer for a question payload
const parseQuestion = (payload = {}) => {
  const normalizedOptions = Array.isArray(payload.options)
    ? payload.options.map((opt) => {
        const raw = opt.isCorrect;
        let isCorrect;
        if (typeof raw === 'boolean') isCorrect = raw;
        else if (raw === 1 || raw === '1' || raw === 'true' || raw === 'TRUE') isCorrect = true;
        else if (raw === 0 || raw === '0' || raw === 'false' || raw === 'FALSE') isCorrect = false;
        else isCorrect = undefined;

        return {
          optionId: opt.optionId ?? opt.questionOptionId ?? opt.id,
          optionText: opt.optionText ?? opt.text ?? '',
          order: Number(opt.optionOrder ?? opt.order ?? 0),
          isCorrect,
        };
      })
    : [];

  return {
    questionId: payload.questionId ?? payload.id,
    quizId: Number(payload.quizId ?? 0),
    skillId:
      payload.skillId != null
        ? Number(payload.skillId)
        : payload.skill?.skillId != null
        ? Number(payload.skill.skillId)
        : 0,
    questionType: payload.questionType || 'single_choice',
    questionText: payload.questionText || '',
    hint: payload.hint ?? null,
    points: Number(payload.points ?? 0),
    timeLimit: payload.timeLimit != null ? Number(payload.timeLimit) : null,
    order: Number(payload.questionOrder ?? payload.order ?? 0),
    isRequired: Boolean(payload.isRequired ?? false),
    explanation: payload.explanation ?? null,
    createdAt: payload.createdAt || new Date().toISOString(),
    updatedAt: payload.updatedAt || new Date().toISOString(),
    options: normalizedOptions,
    // Preserve raw skill object if present for UI display
    skill: payload.skill || null,
  };
};

export const questionQuizReducer = (state = [], action) => {
  switch (action.type) {
    case actions.INITIALIZE_QUIZ_QUESTIONS:
      return Array.isArray(action.payload) ? action.payload.map(parseQuestion) : [];

    case actions.ADD_QUIZ_QUESTION:
      return [...state, parseQuestion(action.payload)];

    case actions.UPDATE_QUIZ_QUESTION: {
      const id = action.payload?.questionId ?? action.payload?.id;
      const idNum = Number(id);
      return state.map((q) => {
        if (Number(q.questionId) !== idNum) return q;
        const next = parseQuestion(action.payload);
        // If options not provided in payload, preserve existing ones to avoid flicker/reversion
        if (!Array.isArray(action.payload?.options)) {
          next.options = q.options;
        }
        return next;
      });
    }

    // When an option is created via CreateOption, also push it into the matching question's
    // embedded options so the UI reflects immediately without a refresh
    case actions.ADD_QUESTION_OPTION: {
      const opt = action.payload || {};
      const qid = Number(opt.questionId ?? opt.question?.questionId ?? 0);
      if (!Number.isFinite(qid) || qid === 0) return state;
      const parseOpt = (o = {}) => {
        const raw = o.isCorrect;
        let isCorrect;
        if (typeof raw === 'boolean') isCorrect = raw;
        else if (raw === 1 || raw === '1' || raw === 'true' || raw === 'TRUE') isCorrect = true;
        else if (raw === 0 || raw === '0' || raw === 'false' || raw === 'FALSE') isCorrect = false;
        else isCorrect = undefined;
        return {
          optionId: o.optionId ?? o.questionOptionId ?? o.id,
          optionText: o.optionText ?? o.text ?? '',
          order: Number(o.optionOrder ?? o.order ?? 0),
          isCorrect,
        };
      };
      return state.map((q) => {
        if (Number(q.questionId) !== qid) return q;
        const nextOptions = Array.isArray(q.options) ? q.options.slice() : [];
        const newOpt = parseOpt(opt);
        // Avoid duplicates by optionId
        const existsIdx = nextOptions.findIndex((x) => Number(x.optionId) === Number(newOpt.optionId));
        if (existsIdx >= 0) nextOptions[existsIdx] = newOpt;
        else nextOptions.push(newOpt);
        nextOptions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        return { ...q, options: nextOptions };
      });
    }

    // Optional: keep embedded options in sync for updates/deletes as well
    case actions.UPDATE_QUESTION_OPTION: {
      const opt = action.payload || {};
      const qid = Number(opt.questionId ?? 0);
      const oid = Number(opt.optionId ?? opt.id);
      if (!Number.isFinite(qid) || !Number.isFinite(oid)) return state;
      const parseOpt = (o = {}) => {
        const raw = o.isCorrect;
        let isCorrect;
        if (typeof raw === 'boolean') isCorrect = raw;
        else if (raw === 1 || raw === '1' || raw === 'true' || raw === 'TRUE') isCorrect = true;
        else if (raw === 0 || raw === '0' || raw === 'false' || raw === 'FALSE') isCorrect = false;
        else isCorrect = undefined;
        return {
          optionId: o.optionId ?? o.questionOptionId ?? o.id,
          optionText: o.optionText ?? o.text ?? '',
          order: Number(o.optionOrder ?? o.order ?? 0),
          isCorrect,
        };
      };
      return state.map((q) => {
        if (Number(q.questionId) !== qid) return q;
        const nextOptions = (Array.isArray(q.options) ? q.options : []).map((x) =>
          Number(x.optionId) === oid ? parseOpt(opt) : x
        );
        nextOptions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        return { ...q, options: nextOptions };
      });
    }

    case actions.DELETE_QUESTION_OPTION: {
      const oid = Number(action.optionId ?? action.id);
      if (!Number.isFinite(oid)) return state;
      return state.map((q) => {
        const before = Array.isArray(q.options) ? q.options : [];
        const after = before.filter((x) => Number(x.optionId) !== oid);
        if (after.length === before.length) return q;
        // Reindex orders to keep consistent visuals
        const sorted = after.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((o, i) => ({ ...o, order: Number.isFinite(o.order) ? o.order : i }));
        return { ...q, options: sorted };
      });
    }

    case actions.DELETE_QUIZ_QUESTION: {
      const id = action.payload?.questionId ?? action.payload?.id ?? action.questionId ?? action.id;
      const idNum = Number(id);
      return state.filter((q) => Number(q.questionId) !== idNum);
    }

    case actions.CLEAR_QUIZ_QUESTIONS:
      return [];

    default:
      return state;
  }
};

export default questionQuizReducer;
